import { GetProp, Image, message, UploadProps } from 'antd';
import { useState } from 'react';
import {
  ImageWrapper,
  Mask,
  MaskButton,
  Placeholder,
  StyledUpload,
} from './styles';
import {
  DeleteOutlined,
  EyeOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { UploadBusinessType, UploadFileType } from '../../types';

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

const getBase64 = (img: FileType, callback: (url: string) => void) => {
  const reader = new FileReader();
  reader.addEventListener('load', () => callback(reader.result as string));
  reader.readAsDataURL(img);
};

const beforeUpload = (file: FileType) => {
  const isValidType =
    file.type === 'image/jpeg' ||
    file.type === 'image/png' ||
    file.type === 'image/bmp';
  if (!isValidType) {
    message.error('You can only upload JPG/PNG/BMP file!');
  }
  const isLt2M = file.size / 1024 / 1024 < 2;
  if (!isLt2M) {
    message.error('Image must smaller than 2MB!');
  }
  return isValidType && isLt2M;
};

const ImageUpload = ({
  fixedWidth = true,
  imageUrl,
  onUploaded,
  onLoadingChange,
  businessType,
}: {
  fixedWidth?: boolean;
  imageUrl?: string;
  onUploaded?: (response?: any) => void;
  onLoadingChange?: (isLoading: boolean) => void;
  businessType?: UploadBusinessType;
}) => {
  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const handleChange: UploadProps['onChange'] = (info) => {
    if (info.file.status === 'uploading') {
      setLoading(true);
      onLoadingChange?.(true);
      return;
    }
    if (info.file.status === 'done') {
      getBase64(info.file.originFileObj as FileType, (url) => {
        setLoading(false);
        onLoadingChange?.(false);
        onUploaded?.(info.file.response);
      });
    }
  };

  const handlePreview = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPreviewOpen(true);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onUploaded?.();
  };

  return (
    <>
      <StyledUpload
        name="files"
        data={{
          businessType,
          fileType: UploadFileType.IMAGE,
        }}
        headers={{
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        }}
        listType="picture-card"
        className={`avatar-uploader ${fixedWidth && 'fixed-width'}`}
        showUploadList={false}
        action={`${process.env.REACT_APP_SERVICE_URL}/file`}
        beforeUpload={beforeUpload}
        onChange={handleChange}
      >
        {imageUrl ? (
          <ImageWrapper>
            <img src={imageUrl} alt="avatar" />
            <Mask className="mask" onClick={(e) => e.stopPropagation()}>
              <MaskButton
                icon={<EyeOutlined />}
                onClick={handlePreview}
                title="Preview"
              />
              <MaskButton
                icon={<DeleteOutlined />}
                onClick={handleDelete}
                title="Delete"
              />
            </Mask>
          </ImageWrapper>
        ) : (
          <button
            style={{ border: 0, background: 'none', cursor: 'pointer' }}
            type="button"
          >
            {loading ? (
              <LoadingOutlined />
            ) : (
              <Placeholder style={{ marginTop: 8 }}>
                Click to Upload Image
              </Placeholder>
            )}
          </button>
        )}
      </StyledUpload>
      <Image
        preview={{
          visible: previewOpen,
          onVisibleChange: (visible) => setPreviewOpen(visible),
        }}
        src={imageUrl}
        style={{ display: 'none' }}
      />
    </>
  );
};

export default ImageUpload;
